import { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  IconButton,
  useTheme,
  Divider,
  Badge,
  Switch,
  FormControlLabel,
  Box
} from '@mui/material';
import {
  CalendarToday,
  PriorityHigh,
  Assignment,
  FilterList,
  Sort,
  ViewList,
  CalendarViewMonth
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';

const UpcomingTask = () => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState('list');
  const [showCompleted, setShowCompleted] = useState(false);

  const [tasks] = useState([
    {
      id: 1,
      title: 'Structural Inspection',
      project: 'Sky Tower Project',
      dueDate: '2024-03-25',
      status: 'Pending',
      priority: 'High',
      assignee: 'John Doe',
      progress: 30
    },
    {
      id: 2,
      title: 'Material Delivery',
      project: 'Riverfront Development',
      dueDate: '2024-03-26',
      status: 'In Progress',
      priority: 'Medium',
      assignee: 'Sarah Smith',
      progress: 65
    },
    {
      id: 3,
      title: 'Safety Audit',
      project: 'Metro Tunnel',
      dueDate: '2024-03-27',
      status: 'Pending',
      priority: 'Critical',
      assignee: 'Mike Johnson',
      progress: 0
    }
  ]);

  const statusColors = {
    'Pending': theme.palette.warning.main,
    'In Progress': theme.palette.info.main,
    'Completed': theme.palette.success.main
  };

  const priorityColors = {
    'Critical': theme.palette.error.main,
    'High': theme.palette.warning.main,
    'Medium': theme.palette.info.main,
    'Low': theme.palette.success.main
  };

  const getDueDateStatus = (dueDate) => {
    const date = parseISO(dueDate);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEE, MMM d');
  };

  const filteredTasks = tasks.filter(task => 
    showCompleted || task.status !== 'Completed'
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
            Upcoming Tasks
          </Typography>
        </Grid>
        
        <Grid item xs={12} md={6} sx={{ 
          display: 'flex', 
          gap: 2,
          justifyContent: 'flex-end',
          alignItems: 'center'
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                color="primary"
              />
            }
            label="Show Completed"
          />
          
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{ ml: 2 }}
          >
            Filters
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<Sort />}
          >
            Sort
          </Button>
          
          <IconButton
            onClick={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}
            color={viewMode === 'list' ? 'primary' : 'default'}
          >
            {viewMode === 'list' ? <CalendarViewMonth /> : <ViewList />}
          </IconButton>
        </Grid>
      </Grid>

      {viewMode === 'list' ? (
        <Grid container spacing={3}>
          {filteredTasks.map((task, index) => (
            <Grid item xs={12} key={task.id}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Paper sx={{
                  p: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-3px)'
                  }
                }}>
                  <Box sx={{ 
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    bgcolor: priorityColors[task.priority] 
                  }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Assignment sx={{ 
                          mr: 1, 
                          color: theme.palette.text.secondary 
                        }} />
                        <Typography variant="h6">
                          {task.title}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary">
                        Project: {task.project}
                      </Typography>
                      
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Chip
                          label={task.status}
                          size="small"
                          sx={{
                            backgroundColor: `${statusColors[task.status]}20`,
                            color: statusColors[task.status]
                          }}
                        />
                        <Chip
                          label={task.priority}
                          size="small"
                          sx={{
                            backgroundColor: `${priorityColors[task.priority]}20`,
                            color: priorityColors[task.priority]
                          }}
                          icon={<PriorityHigh fontSize="small" />}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <Box sx={{ textAlign: { md: 'right' } }}>
                          <Typography variant="caption" color="text.secondary">
                            Due Date
                          </Typography>
                          <Typography variant="body1">
                            {getDueDateStatus(task.dueDate)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ 
                          mt: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <Chip
                            label={task.assignee}
                            size="small"
                            variant="outlined"
                          />
                          <Badge
                            color="primary"
                            badgeContent={`${task.progress}%`}
                            sx={{ ml: 2 }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 3, minHeight: 400 }}>
          <Typography variant="h6" color="text.secondary">
            Calendar View (Implementation Needed)
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default UpcomingTask;